import React, { Dispatch, useEffect, useMemo, useRef, useState } from 'react';
import { DateRange } from 'react-date-range';
import { css } from '@emotion/react';
import { StyledProps } from '@components/atoms/Inputs/Select';
import {
  PickerIcon,
  DateRangeWrapper,
  DateRangeInput,
  DateRangeSection,
  DateRangeStatic,
  DateRangeButton,
} from './style';

interface DateProps {
  date: Date;
  format?: ViewFormat;
}

export enum ViewFormat {
  Dot,
  Semi,
}

export function getYYYYMMDDFormat(date?: Date) {
  if (!date) return '';
  const year = date.getFullYear().toString();
  const month = 1 + date.getMonth();
  const monthStr = `0${month}`.slice(-2);
  const day = date.getDate();
  const dayStr = `0${day}`.slice(-2);

  return `${year}-${monthStr}-${dayStr}`;
}

export const getYYYYMMDD = ({ date, format }: DateProps) => {
  const year = date.getFullYear();
  const month: number = 1 + date.getMonth();
  let day: number = date.getDate();
  const monthStr = `0${month}`.slice(-2);
  const dayStr = `0${day}`.slice(-2);
  if (format === ViewFormat.Semi) return year + '-' + monthStr + '-' + dayStr;
  if (format) return year + '.' + monthStr + '.' + dayStr;
  return year + '' + monthStr + dayStr;
};

type DataRangeMenuProps<T = any> = {
  defaultValue?: any;
  dateRangeSet: any;
  isTabVisible?: boolean;
  setDateRangeSet: Dispatch<any>;
  placeholder?: string;
  border?: boolean;
  maxDate?: Date;
  minDate?: Date;
  hasYearOption?: boolean;
  onClick?: (e: any) => void;
  isReadOnly?: boolean;
  setSearchConditions?: React.Dispatch<React.SetStateAction<T>>;
  filterKey?: string;
  doSearch?: () => void;
} & StyledProps;

const DATE_REGEX = {
  START: {
    yyyyMMdd: /^(\d{4})(\d{2})(\d{2})/,
    'yyyy-MM-dd': /^(\d{4})-(\d{2})-(\d{2})/,
  },
  END: {
    yyyyMMdd: /(\d{4})(\d{2})(\d{2})$/,
    'yyyy-MM-dd': /(\d{4})-(\d{2})-(\d{2})$/,
  },
  BETWEEN: {
    '~': /~+/g,
    'yyyy-MM-dd~yyyy-MM-dd': /^(\d{4})-(\d{2})-(\d{2})~(\d{4})-(\d{2})-(\d{2})$/,
  },
};

const DataRangeMenu = ({
  defaultValue,
  dateRangeSet,
  isTabVisible = true,
  setDateRangeSet,
  placeholder = '',
  styles = css``,
  border = false,
  minDate,
  maxDate = new Date(),
  hasYearOption = false,
  onClick,
  isReadOnly,
  setSearchConditions,
  filterKey = '',
  doSearch,
}: DataRangeMenuProps) => {
  const rangeEl = useRef<any>(null);
  const inputEl = useRef<any>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [dateInputText, setDateInputText] = useState({
    value:
      defaultValue?.dateRange?.startDate && defaultValue?.dateRange?.endDate
        ? `${defaultValue.dateRange.startDate}~${defaultValue.dateRange.endDate}`
        : '',
    startSelection: 0,
  });
  const [selectedStaticRange, setSelectedStaticRange] = useState('');

  /**
   * datarange 자동 변경
   */
  const onDateFilter = (date: string, num: number, setDataRange: any, e: React.MouseEvent) => {
    setSelectedStaticRange((e as any).target.dataset.value);
    let today = new Date();
    let calday = new Date();
    let tmpDate = {
      startDate: calday,
      endDate: new Date(),
      key: 'selection',
    };

    if (date === 'day') {
      calday.setDate(calday.getDate() - num);
      setDataRange([{ ...tmpDate, startDate: calday }]);

      const startDate = getYYYYMMDDFormat(calday);
      const endDate = getYYYYMMDDFormat(today);
      setDateInputText((prev) => ({
        ...prev,
        value: `${startDate}~${endDate}`,
      }));
    } else if (date === 'month') {
      calday.setMonth(calday.getMonth() - num);
      calday.setDate(calday.getDate() + 1);
      setDataRange([{ ...tmpDate, startDate: calday }]);

      const startDate = getYYYYMMDDFormat(calday);
      const endDate = getYYYYMMDDFormat(today);
      setDateInputText((prev) => ({
        ...prev,
        value: `${startDate}~${endDate}`,
      }));
    } else if (date === 'year') {
      calday.setFullYear(calday.getFullYear() - num);
      calday.setMonth(calday.getMonth());
      calday.setDate(calday.getDate() + 1);
      setDataRange([{ ...tmpDate, startDate: calday }]);

      const startDate = getYYYYMMDDFormat(calday);
      const endDate = getYYYYMMDDFormat(today);
      setDateInputText((prev) => ({
        ...prev,
        value: `${startDate}~${endDate}`,
      }));
    } else if (date === 'all') {
      setDataRange([{ ...tmpDate, startDate: null }]);
      setDateInputText((prev) => ({
        ...prev,
        value: ``,
      }));
    }
  };

  const onDateTextChange = (e: any) => {
    setSelectedStaticRange('');
    setSearchConditions &&
      setSearchConditions((prev: any) => {
        return {
          ...prev,
          selectedStaticRange: 'custom',
        };
      });
    const value = e.target.value;

    let resText = '';

    if (value.length !== 0) {
      resText = value
        .replace(DATE_REGEX.START['yyyyMMdd'], '$1-$2-$3')
        .replace(DATE_REGEX.START['yyyy-MM-dd'], '$1-$2-$3~')
        .replace(DATE_REGEX.END['yyyyMMdd'], '$1-$2-$3')
        .replace(DATE_REGEX.END['yyyy-MM-dd'], '~$1-$2-$3')
        .replace(DATE_REGEX.BETWEEN['~'], '~');

      setDateInputText((prev) => ({
        ...prev,
        value: resText,
        startSelection: 0,
      }));

      if (DATE_REGEX.BETWEEN['yyyy-MM-dd~yyyy-MM-dd'].test(resText)) {
        const [start, end] = resText.split('~');
        const startDate = new Date(start);
        const endDate = new Date(end) > new Date() ? new Date() : new Date(end);

        if (start <= end && startDate.toString() !== 'Invalid Date' && endDate.toString() !== 'Invalid Date') {
          let tmpDate = {
            startDate: startDate,
            endDate: endDate,
            key: 'selection',
          };

          setDateRangeSet([{ ...tmpDate }]);
        }
      }
    } else {
      setDateInputText((prev) => ({
        ...prev,
        value: resText,
        startSelection: 0,
      }));

      let tmpDate = {
        startDate: null,
        endDate: null,
        key: 'selection',
      };

      setDateRangeSet([{ ...tmpDate }]);
    }
  };

  const onDateRangeChange = (item: any) => {
    setSelectedStaticRange('');

    setSearchConditions &&
      setSearchConditions((prev: any) => {
        return {
          ...prev,
          selectedStaticRange: 'custom',
        };
      });

    const startDate = getYYYYMMDDFormat(item.selection.startDate);
    const endDate = getYYYYMMDDFormat(item.selection.endDate);
    setDateRangeSet([item.selection]);
    setDateInputText((prev) => ({
      ...prev,
      value: `${startDate}~${endDate}`,
      startSelection: prev.startSelection + 1,
    }));

    if (startDate !== endDate) {
      setIsVisible(false);
    }
  };
  const handleClickOutside = ({ target }: MouseEvent) => {
    if (target) {
      if (isVisible && !rangeEl.current?.contains(target)) setIsVisible(false);
    }
  };

  useEffect(() => {
    addEventListener('click', handleClickOutside);
    return () => {
      removeEventListener('click', handleClickOutside);
    };
  });

  useEffect(() => {
    if (dateInputText.startSelection > 1) {
      doSearch && doSearch();

      setDateInputText((prev) => ({
        ...prev,
        startSelection: 0,
      }));
    }
  }, [dateInputText.startSelection]);

  const value = useMemo(() => {
    const start = getYYYYMMDDFormat(dateRangeSet[0].startDate);
    const end = getYYYYMMDDFormat(dateRangeSet[0].endDate);

    if (defaultValue && defaultValue.selectedStaticRange === 'custom' && !dateInputText.value) return 'All';
    if (!start || !end) return '';
    return `${start}~${end}`;
  }, [dateRangeSet, defaultValue, dateInputText]);

  return (
    <DateRangeWrapper
      className={`${border ? 'border' : ''} ${isVisible ? 'show-border' : ''} ${filterKey}`}
      ref={rangeEl}
      styles={styles}
    >
      <DateRangeInput
        placeholder={placeholder}
        type="text"
        value={value}
        onChange={onDateTextChange}
        data-value={'custom'}
        onClick={(e) => {
          setIsVisible(true);
          onClick;
          setSearchConditions &&
            setSearchConditions((prev: any) => {
              return {
                ...prev,
                selectedStaticRange: 'custom',
              };
            });
        }}
        ref={inputEl}
        readOnly={isReadOnly}
      />
      {isVisible && (
        <DateRangeSection data-value={'custom'}>
          <div className="range-group">
            {isTabVisible ? (
              <DateRangeStatic data-selected={selectedStaticRange}>
                <DateRangeButton data-value={'today'} onClick={(e) => onDateFilter('day', 0, setDateRangeSet, e)}>
                  Today
                </DateRangeButton>
                {!hasYearOption && (
                  <DateRangeButton data-value={'threeday'} onClick={(e) => onDateFilter('day', 2, setDateRangeSet, e)}>
                    3D
                  </DateRangeButton>
                )}

                <DateRangeButton data-value={'oneweek'} onClick={(e) => onDateFilter('day', 6, setDateRangeSet, e)}>
                  1w
                </DateRangeButton>
                <DateRangeButton data-value={'onemonth'} onClick={(e) => onDateFilter('month', 1, setDateRangeSet, e)}>
                  1M
                </DateRangeButton>
                {hasYearOption && (
                  <DateRangeButton data-value={'oneyear'} onClick={(e) => onDateFilter('year', 1, setDateRangeSet, e)}>
                    1Y
                  </DateRangeButton>
                )}
                <DateRangeButton data-value={'all'} onClick={(e) => onDateFilter('all', 0, setDateRangeSet, e)}>
                  All
                </DateRangeButton>
              </DateRangeStatic>
            ) : null}
            <DateRange
              editableDateInputs={true}
              onChange={onDateRangeChange}
              weekdayDisplayFormat={'EEEEE'}
              moveRangeOnFirstSelection={false}
              ranges={dateRangeSet}
              showDateDisplay={false}
              minDate={minDate}
              maxDate={maxDate}
              showMonthAndYearPickers={false}
            />
          </div>
        </DateRangeSection>
      )}
      <PickerIcon onClick={() => setIsVisible(true)} />
    </DateRangeWrapper>
  );
};

export default DataRangeMenu;
